import { ChevronDown, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CONTENT_FORMAT_LABELS, CONTENT_FORMATS, type ContentFormat } from '@/lib/contentFormat';

interface EditorFormatSelectProps {
  value: ContentFormat | undefined;
  onChange: (format: ContentFormat) => void;
  className?: string;
}

/**
 * A compact "what format am I editing?" selector shown next to the
 * editor's toolbar.
 *
 * Before this existed, only two editors (Djot and AsciiDoc) were
 * hardcoded into their respective edit pages, and the user had no way to
 * tell which markup they were using — an especially confusing state when
 * a fork or a misdetected format chose the wrong one. This makes the
 * format explicit and changeable: Djot, AsciiDoc, Markdown, or Plain
 * text.
 *
 * Markdown and Plain text don't have their own dedicated editors — see
 * `editorFormatFor` — they map to the closest editor (Djot) since that's
 * the best available approximation. Djot and Markdown deliberately share
 * `#` ATX headings and `[text](url)` links, so the Djot editor handles
 * both without producing broken markup.
 */
export function EditorFormatSelect({ value, onChange, className }: EditorFormatSelectProps) {
  const label = value ? CONTENT_FORMAT_LABELS[value] : 'Format';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" className={className}>
          <FileText className="size-4" />
          {label}
          <ChevronDown className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {CONTENT_FORMATS.map((format) => (
          <DropdownMenuItem key={format} onSelect={() => onChange(format)}>
            <span className={format === value ? 'font-medium' : ''}>{CONTENT_FORMAT_LABELS[format]}</span>
            {format === value && <span className="ml-auto text-xs text-muted-foreground">current</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
